import React, { useState } from 'react';
import _intersection from 'lodash/intersection';
import _groupBy from 'lodash/groupBy';

import { useBookmarks } from 'services/bookmark';
import type { IdBookmark, PqlBookmark, BookmarkType } from 'services/bookmark';
import { AlertIcon, IndicatorIcon, ClipboardIcon, LogIcon } from 'components/common/Icons';
import ModalLoader from 'components/common/Loader/ModalLoader';
import Tabs from 'components/common/Tabs';
import { Subtitle } from 'components/common/PageList';
import { SearchInput } from 'components/common/PageList';
import BookmarkItem, { NoBookmarks } from 'components/common/BookmarkItem';
import Scrollbar from 'components/common/Scrollbar';
import { filterBookmarks } from 'utils/bookmarks';
import { cn } from 'utils/styles';
type BookmarkSearchType = IdBookmark['searchType'];

const getScrollbarHeight = (hasEditingBookmark: boolean, isSearchesTab: boolean) => ({
  height: hasEditingBookmark && isSearchesTab ? 600 : 400,
});

const SectionIcon = ({ searchType }: { searchType: BookmarkSearchType }) => {
  switch (searchType) {
    case 'O_ALERTS':
      return <AlertIcon />;
    case 'O_EVENTS':
      return <ClipboardIcon />;
    case 'O_INDICATORS':
      return <IndicatorIcon />;
    default:
      return <LogIcon />;
  }
};

const sectionTitles: Record<BookmarkType, Partial<Record<BookmarkSearchType, string>>> = {
  id: {
    O_ALERTS: 'Alerts',
    O_EVENTS: 'Events',
    O_INDICATORS: 'Indicators',
    O_SENSOR: 'Logs',
  },
  pql: {
    O_EVENTS: 'Event queries',
    O_INDICATORS: 'Indicator queries',
    O_SENSOR: 'Log queries',
  },
};

type BookmarkSectionProps = {
  bookmarks: (IdBookmark | PqlBookmark)[];
  bookmarkType: BookmarkType;
  searchType: BookmarkSearchType;
  onEditingChange: (editing: boolean) => void;
};

const BookmarkSection = React.memo(({ bookmarks, bookmarkType, searchType, onEditingChange }: BookmarkSectionProps) => {
  return (
    <div className="pr-2 not-first-of-type:mt-4.5">
      <Subtitle className="flex items-center imr-2 mt-1">
        <SectionIcon searchType={searchType} />
        {sectionTitles[bookmarkType][searchType]}
      </Subtitle>
      {bookmarks.map((b) => (
        <BookmarkItem key={b.id} type={bookmarkType} bookmark={b} onEditingChange={onEditingChange} />
      ))}
    </div>
  );
});

type BookmarksTabProps = {
  type: BookmarkType;
};

const BookmarksTab = ({ type }: BookmarksTabProps) => {
  const bookmarks = useBookmarks(type);

  const [filter, setFilter] = useState<string>();
  const [hasEditingBookmark, setHasEditingBookmark] = useState(false);

  const groupedBookmarks = bookmarks.data
    ? _groupBy(filterBookmarks(bookmarks.data, filter), 'searchType')
    : ({} as Record<string, (IdBookmark | PqlBookmark)[]>);
  const availableSearchTypes = _intersection(
    ['O_ALERTS', 'O_EVENTS', 'O_INDICATORS', 'O_SENSOR'] as BookmarkSearchType[],
    Object.keys(groupedBookmarks),
  ) as BookmarkSearchType[];

  return (
    <div className="mt-3">
      <div className={cn('flex justify-end mb-3', bookmarks.data?.length && '-mt-10.5')}>
        <SearchInput collection={bookmarks.data ?? []} placeholder="search..." onChange={setFilter} />
      </div>
      <Scrollbar style={getScrollbarHeight(hasEditingBookmark, type === 'pql')}>
        {bookmarks.isLoading ? (
          <ModalLoader />
        ) : bookmarks.data?.length ? (
          availableSearchTypes.length === 0 ? (
            <NoBookmarks title="No results" description="No bookmarks were found for the given query." />
          ) : (
            availableSearchTypes.map((searchType) => (
              <BookmarkSection
                key={searchType}
                bookmarkType={type}
                bookmarks={groupedBookmarks[searchType]}
                searchType={searchType}
                onEditingChange={setHasEditingBookmark}
              />
            ))
          )
        ) : (
          <NoBookmarks
            title="No bookmarks yet"
            description="Your bookmarked records and queries will be displayed here."
          />
        )}
      </Scrollbar>
    </div>
  );
};

const tabs = [
  {
    tabName: 'Records',
    alias: 'id' as const,
    component: BookmarksTab,
  },
  {
    tabName: 'Searches',
    alias: 'pql' as const,
    component: BookmarksTab,
  },
];

const BookmarksData = () => {
  return (
    <Tabs
      queryParam={undefined}
      components={tabs.map((tab) => ({
        ...tab,
        component: (props: Record<string, unknown>) => <tab.component {...props} type={tab.alias} />,
      }))}
    />
  );
};

export default React.memo(BookmarksData);
